import type { MouseEvent as ReactMouseEvent, TouchEvent as ReactTouchEvent } from 'react'
import { MouseSensor, TouchSensor, type MouseSensorOptions, type TouchSensorOptions } from '@dnd-kit/core'

/**
 * Drag sensors for a card you can pick up anywhere.
 *
 * The drag used to live on the grip alone, which made the rest of the card
 * inert and the grip a target you had to hit. The whole card is the target
 * now, which raises two problems the stock sensors do not solve:
 *
 *  - A card has buttons on it. A press on the thumbnail should open the photo
 *    and a press on the ✕ should remove the item; neither should start a drag.
 *    Anything inside `[data-no-drag]` is skipped.
 *
 *  - On a phone the cards cover most of the board, so if touching one always
 *    began a drag there would be nothing left to scroll with. Mouse and touch
 *    are separate sensors with different constraints (see PlanView): a mouse
 *    drags as soon as it moves, a finger has to rest for a moment first, so a
 *    swipe still scrolls the board and a press picks a card up.
 */

function insideNoDrag(event: { nativeEvent: Event }): boolean {
  const target = event.nativeEvent.target
  return target instanceof Element && target.closest('[data-no-drag]') !== null
}

/*
 * Both delegate to the library's own activator rather than reimplementing it,
 * so the checks it already does -- a right-click, a second finger -- keep
 * working and keep working when the library changes them.
 */

export class CardMouseSensor extends MouseSensor {
  static activators = [
    {
      eventName: 'onMouseDown' as const,
      handler: (event: ReactMouseEvent, options: MouseSensorOptions) =>
        insideNoDrag(event) ? false : MouseSensor.activators[0]!.handler(event, options),
    },
  ]
}

export class CardTouchSensor extends TouchSensor {
  static activators = [
    {
      eventName: 'onTouchStart' as const,
      handler: (event: ReactTouchEvent, options: TouchSensorOptions) =>
        insideNoDrag(event) ? false : TouchSensor.activators[0]!.handler(event, options),
    },
  ]
}
